import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Mirrors the form_route pattern: state lives in DOM rows, every mutation calls
// renumber() to (a) reassign `article[images_attributes][KEY][...]` names and
// (b) refresh the position hidden field on still-active rows.
export default class extends Controller {
  static targets = ["picker", "list", "error"]
  static values = { existing: String }

  connect() {
    this.newCounter = 0
    this.errorTarget.textContent = ""
    this.renderExisting()
    this.sortable = Sortable.create(this.listTarget, {
      animation: 150,
      handle: ".article-gallery-admin__handle",
      draggable: ".article-gallery-admin__row:not(.is-destroyed)",
      ghostClass: "sortable-ghost",
      onEnd: () => this.renumber()
    })
    this.renumber()
  }

  disconnect() {
    this.sortable?.destroy()
  }

  renderExisting() {
    if (!this.existingValue) return
    let items
    try { items = JSON.parse(this.existingValue) } catch (e) { return }
    items.forEach((it) => {
      this.appendRow({
        key: String(it.id),
        idValue: it.id,
        thumbUrl: it.thumb_url,
        description: it.description || "",
        position: it.position
      })
    })
  }

  filesPicked(event) {
    this.errorTarget.textContent = ""
    const files = Array.from(event.target.files || [])
    files.forEach((file) => {
      // Rails 8 strong-params only accepts all-numeric keys under nested attributes;
      // use a long numeric id that can't collide with existing DB ids (timestamp * 1000 + counter).
      const key = String(Date.now() * 1000 + this.newCounter++)
      this.appendRow({
        key,
        file,
        thumbUrl: URL.createObjectURL(file),
        description: "",
        position: this.listTarget.children.length
      })
    })
    this.pickerTarget.value = ""
    this.renumber()
  }

  appendRow({ key, idValue, file, thumbUrl, description, position }) {
    const row = document.createElement("div")
    row.className = "article-gallery-admin__row"
    row.dataset.key = key

    const handle = document.createElement("span")
    handle.className = "article-gallery-admin__handle"
    handle.textContent = "⠿"
    handle.title = "Přetáhněte pro změnu pořadí"
    row.appendChild(handle)

    const img = document.createElement("img")
    img.className = "article-gallery-admin__thumb"
    img.src = thumbUrl
    img.alt = ""
    row.appendChild(img)

    const descWrap = document.createElement("div")
    descWrap.className = "article-gallery-admin__desc"
    const desc = document.createElement("input")
    desc.type = "text"
    desc.className = "form-control form-control-sm"
    desc.placeholder = "Popisek"
    desc.value = description
    desc.dataset.field = "description"
    descWrap.appendChild(desc)
    row.appendChild(descWrap)

    const positionInput = document.createElement("input")
    positionInput.type = "hidden"
    positionInput.dataset.field = "position"
    positionInput.value = position ?? 0
    row.appendChild(positionInput)

    if (idValue !== undefined) {
      const idInput = document.createElement("input")
      idInput.type = "hidden"
      idInput.dataset.field = "id"
      idInput.value = idValue
      row.appendChild(idInput)

      const destroyInput = document.createElement("input")
      destroyInput.type = "hidden"
      destroyInput.dataset.field = "_destroy"
      destroyInput.value = ""
      row.appendChild(destroyInput)
    } else if (file) {
      const fileInput = document.createElement("input")
      fileInput.type = "file"
      fileInput.style.display = "none"
      fileInput.dataset.field = "image"
      const dt = new DataTransfer()
      dt.items.add(file)
      fileInput.files = dt.files
      row.appendChild(fileInput)
    }

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "btn btn-sm btn-outline-danger ms-auto"
    remove.textContent = "×"
    remove.setAttribute("aria-label", "Odebrat obrázek")
    remove.addEventListener("click", () => this.removeRow(row))
    row.appendChild(remove)

    this.listTarget.appendChild(row)
  }

  removeRow(row) {
    const destroy = row.querySelector('[data-field="_destroy"]')
    if (destroy) {
      destroy.value = "1"
      row.classList.add("is-destroyed")
    } else {
      row.remove()
    }
    this.renumber()
  }

  renumber() {
    let activeIdx = 0
    Array.from(this.listTarget.children).forEach((row) => {
      const isDestroyed = row.classList.contains("is-destroyed")
      const key = row.dataset.key
      const fields = row.querySelectorAll("[data-field]")
      fields.forEach((input) => {
        const fieldName = input.dataset.field
        input.name = `article[images_attributes][${key}][${fieldName}]`
        if (fieldName === "position" && !isDestroyed) {
          input.value = activeIdx
        }
      })
      if (!isDestroyed) activeIdx++
    })
  }
}
